import { Check, FileText, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api, getApiErrorMessage } from '../../api/client';
import type { KybDocument, MerchantRow } from '../../api/types';
import { isPendingKyb } from '../../utils/merchantStatus';

export function KybSection() {
  const [pending, setPending] = useState<MerchantRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [documents, setDocuments] = useState<KybDocument[]>([]);
  const [detailMerchant, setDetailMerchant] = useState<MerchantRow | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await api.listMerchants();
      const list = res.merchants.filter((m) => isPendingKyb(m.status, m.kyb_status));
      setPending(list);
      setError(null);
      if (list.length > 0 && !selectedId) setSelectedId(list[0].id);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadDetail = useCallback(async (merchantId: string) => {
    setLoadingDetail(true);
    try {
      const res = await api.getMerchantKyb(merchantId);
      setDetailMerchant(res.merchant);
      setDocuments(res.documents);
    } catch (e) {
      setError(getApiErrorMessage(e));
      setDocuments([]);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedId) void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const runKybAction = async (action: 'approve' | 'reject') => {
    if (!selectedId) return;
    setActionLoading(true);
    setError(null);
    try {
      await api.patchMerchantKyb(selectedId, action);
      setSelectedId(null);
      setDetailMerchant(null);
      setDocuments([]);
      await loadList();
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <div className="section-header">
        <h1 className="console-title">KYB Center</h1>
        <p className="console-sub">Compliance review · pending applications</p>
      </div>

      {error ? <p className="form-error section-error">{error}</p> : null}

      <div className="kyb-layout">
        <div className="card kyb-list-card">
          <div className="section-title">Pending merchants</div>
          {loadingList ? (
            <p className="modal-muted">Loading…</p>
          ) : pending.length === 0 ? (
            <p className="modal-muted">No pending KYB cases</p>
          ) : (
            <ul className="kyb-list">
              {pending.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className={`kyb-list-item${selectedId === m.id ? ' active' : ''}`}
                    onClick={() => setSelectedId(m.id)}
                  >
                    <span className="cell-primary">{m.name}</span>
                    <span className="cell-muted">{m.solana_wallet.slice(0, 16)}…</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card kyb-detail-card">
          {!selectedId ? (
            <p className="modal-muted">Select a merchant to review documents</p>
          ) : loadingDetail ? (
            <p className="modal-muted">Loading documents…</p>
          ) : (
            <>
              <h2 className="kyb-detail-title">{detailMerchant?.name}</h2>
              <p className="modal-muted">Review uploaded KYC documents</p>
              <div className="kyb-docs">
                {documents.length === 0 ? (
                  <p className="modal-muted">No documents on file</p>
                ) : (
                  documents.map((doc) => (
                    <div key={doc.type} className="kyb-doc-row">
                      <FileText size={18} color="var(--onyx-gold)" aria-hidden />
                      <div>
                        <div className="cell-primary">{doc.label}</div>
                        {doc.url ? (
                          <a href={doc.url} target="_blank" rel="noreferrer" className="kyb-doc-link">
                            View document
                          </a>
                        ) : (
                          <span className="cell-muted">Not uploaded</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="kyb-actions">
                <button
                  type="button"
                  className="btn-primary btn-inline kyb-approve"
                  disabled={actionLoading}
                  onClick={() => void runKybAction('approve')}
                >
                  <Check size={16} aria-hidden /> Approve
                </button>
                <button
                  type="button"
                  className="btn-danger btn-inline"
                  disabled={actionLoading}
                  onClick={() => void runKybAction('reject')}
                >
                  <X size={16} aria-hidden /> Reject
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
